import { inject, Injectable } from '@angular/core';
import { first, Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';

import {
  CreateOrderRequest,
  Order,
  OrderExceptions,
  OrderFilters,
  OrderForSelect,
  OrderShipmentOption,
  UpdateOrderPayload,
} from '@core/models/order.model';
import { ApiService } from '@core/services/api.service';
import { PaginatedResponse } from '@core/models/response';
import { LoadingService } from './loading';
import { SnackbarService } from './snackbar.service';
import { OrderLoaderKey } from '@core/enums';
import { cleanHTTPParams } from '@core/utils/helpers';
import { CommentableResourceService } from '@core/interfaces/commentable-resource.interface';
import { Comment } from '@core/models/comment.model';
import { CommentLoaderKey } from '@core/enums/comment.enum';
import { Attachment } from '@core/models';

const endpoint = 'v1/orders';

@Injectable({
  providedIn: 'root',
})
export class OrderService implements CommentableResourceService {
  api = inject(ApiService);
  loader = inject(LoadingService);
  snack = inject(SnackbarService);

  get(id: string): Observable<Order> {
    return this.api
      .get<Order>(`${endpoint}/${id}`)
      .pipe(first(), this.loader.set(OrderLoaderKey.GET_ORDER), this.snack.operator());
  }

  list(filters: OrderFilters): Observable<PaginatedResponse<Order>> {
    const params = new HttpParams({ fromObject: cleanHTTPParams(filters) });
    return this.api
      .get<PaginatedResponse<Order>>(endpoint, { params })
      .pipe(first(), this.loader.set(OrderLoaderKey.LIST_ORDERS), this.snack.operator());
  }

  create(payload: CreateOrderRequest, file: File): Observable<Order> {
    const requestData: FormData = this.prepareFormData(payload, file);
    return this.api
      .post<Order, unknown>(endpoint, requestData)
      .pipe(first(), this.loader.set(OrderLoaderKey.CREATE_ORDER), this.snack.operator());
  }

  update(id: string, payload: UpdateOrderPayload): Observable<void> {
    return this.api
      .patch<void, UpdateOrderPayload>(`${endpoint}/${id}`, payload)
      .pipe(first(), this.loader.set(OrderLoaderKey.UPDATE_ORDER), this.snack.operator('Order updated'));
  }

  updateProduct(payload: unknown): Observable<unknown> {
    return this.api.patch(`${endpoint}/product`, payload).pipe(first());
  }

  linkShipment(orderId: string, shipmentId: string, link: boolean): Observable<void> {
    return this.api
      .post<void, { link: boolean }>(`${endpoint}/${orderId}/link-shipment/${shipmentId}`, { link })
      .pipe(first(), this.loader.set(OrderLoaderKey.LINK_SHIPMENT));
  }

  listShipmentOptions(params: { senderId?: string; receiverId?: string }): Observable<OrderShipmentOption[]> {
    const httpParams = new HttpParams({ fromObject: cleanHTTPParams(params) });
    return this.api
      .get<OrderShipmentOption[]>(`${endpoint}/shipments/select`, { params: httpParams })
      .pipe(first());
  }

  getExceptions(): Observable<OrderExceptions> {
    return this.api
      .get<OrderExceptions>(`${endpoint}/exceptions`)
      .pipe(first(), this.snack.operator());
  }

  listComments(resourceId: string): Observable<Comment[]> {
    return this.api
      .get(`${endpoint}/${resourceId}/comments`)
      .pipe(first(), this.loader.set(CommentLoaderKey.LIST_COMMENTS), this.snack.operator());
  }

  createComment(resourceId: string, formData: FormData): Observable<Comment> {
    return this.api
      .post(`${endpoint}/${resourceId}/comments`, formData)
      .pipe(first(), this.loader.set(CommentLoaderKey.CREATE_COMMENT), this.snack.operator());
  }

  listAttachments(resourceId: string): Observable<Attachment[]> {
    return this.api
      .get(`${endpoint}/${resourceId}/attachments`)
      .pipe(first(), this.loader.set(CommentLoaderKey.LIST_COMMENTS), this.snack.operator());
  }

  createAttachment(resourceId: string, file: File): Observable<Attachment> {
    const formData: FormData = new FormData();
    formData.set('file', file);
    return this.api
      .post(`${endpoint}/${resourceId}/attachments`, formData)
      .pipe(first(), this.loader.set(CommentLoaderKey.CREATE_COMMENT), this.snack.operator());
  }

  listWithoutPagination(): Observable<Order[]> {
    return this.api
      .get<OrderForSelect[]>(`${endpoint}/select/all`)
      .pipe(first(), this.loader.set(OrderLoaderKey.LIST_ORDERS), this.snack.operator());
  }

  private prepareFormData(order: CreateOrderRequest, file: File): FormData {
    const formData: FormData = new FormData();
    formData.set('file', file);
    formData.set('receiverId', order.receiverId);
    if (order.comment) formData.set('comment', order.comment);
    formData.set('senderRef', order.senderRef);
    formData.append('products', JSON.stringify(order.products));
    return formData;
  }
}
